import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@contexts/AuthContext';
import {
  Search,
  Edit,
  RefreshCw,
  AlertTriangle,
  Eye,
  CheckCircle,
  XCircle,
  Shield,
  UserPlus,
  Users,
  Wifi,
  WifiOff,
} from 'lucide-react';
import { usersApi } from '@domains/admin/api/users.api';
import { impersonationApi } from '@domains/admin/api/impersonation.api';
import { notify } from '../../../core/notifications/notify';
import { useModal, AdminUserModal, ConfirmModal } from '../../../components/modal';
import { socketService } from '../../../services/socket.service';
import type { UserWithRoles } from '@domains/admin/api/users.api';
import PageInfoButton from '@/components/PageInfoButton';

const CompanyUsers: React.FC = () => {
  const { user, hasPermission, isLoading: authLoading } = useAuth();
  const { openModal, closeModal } = useModal();
  const [users, setUsers] = useState<UserWithRoles[]>([]);
  const [availablePermissions, setAvailablePermissions] = useState<string[]>([]);
  const [permissionProfiles, setPermissionProfiles] = useState<{ [key: number]: string }>({});

  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });

  const isCompanyAdmin = user?.permissions?.includes('users.create') || false;

  const loadPermissionProfiles = async () => {
    try {
      const profiles = await usersApi.getUserTypes(true);
      // Company admins should only see company-scoped roles they can assign
      // to their employees. Platform roles (System Admin, Partner Admin) and
      // system-level roles (External Auditor — assigned by System Admin via
      // auditor_assignments) are not company-manageable.
      const COMPANY_ROLES = new Set(['Company Admin', 'Compliance User', 'Internal Auditor']);
      const filtered = user?.company_id
        ? profiles.filter((p: any) => COMPANY_ROLES.has(String(p.name)))
        : profiles;
      const profileMap: { [key: number]: string } = {};
      filtered.forEach((profile: any) => {
        profileMap[profile.id] = profile.name;
      });
      setPermissionProfiles(profileMap);
    } catch (error) {
      console.error('Error loading permission profiles:', error);
      setPermissionProfiles({});
    }
  };

  useEffect(() => {
    loadUsers();
    loadAvailablePermissions();
    loadPermissionProfiles();

    const savedScrollPosition = sessionStorage.getItem('company-users-page-scroll-position');
    if (savedScrollPosition) {
      setTimeout(() => {
        const mainElement = document.querySelector('main');
        if (mainElement) {
          mainElement.scrollTop = parseInt(savedScrollPosition, 10);
        }
      }, 100);
    }

    if (user?.id) {
      socketService.connect(user.id);
    }

    const handleUserStatusChange = (event: CustomEvent) => {
      const { userId, status, lastLoginAt, lastLoginIp } = event.detail;
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === userId
            ? {
                ...user,
                is_online: status === 'online',
                last_login_at: lastLoginAt || user.last_login_at,
                last_login_ip: lastLoginIp || user.last_login_ip,
              }
            : user,
        ),
      );
    };

    window.addEventListener('userStatusChanged', handleUserStatusChange as EventListener);

    const handleScroll = () => {
      const mainElement = document.querySelector('main');
      if (mainElement) {
        sessionStorage.setItem('company-users-page-scroll-position', mainElement.scrollTop.toString());
      }
    };

    const mainElement = document.querySelector('main');
    if (mainElement) {
      mainElement.addEventListener('scroll', handleScroll);
    }

    return () => {
      window.removeEventListener('userStatusChanged', handleUserStatusChange as EventListener);
      if (mainElement) {
        mainElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, [user?.id]);

  const loadUsers = async () => {
    setIsLoading(true);
    try {
      if (user?.company_id) {
        const result = await usersApi.getUsersByCompany(user.company_id);
        setUsers(result);
        setPagination({
          page: 1,
          limit: result.length,
          total: result.length,
          pages: 1,
        });
      }
    } catch (error) {
      console.error('Error loading users:', error);
      notify.error('Failed to load users');
    } finally {
      setIsLoading(false);
    }
  };

  const loadAvailablePermissions = async () => {
    try {
      const permissions = await usersApi.getAssignablePermissions();
      setAvailablePermissions(permissions);
    } catch (error) {
      console.error('Error loading permissions:', error);
      setAvailablePermissions([]);
    }
  };

  const handleSaveUser = async (userData: any) => {
    try {
      if (userData.id !== null) {
        const updateData: any = {
          first_name: userData.first_name,
          last_name: userData.last_name,
          is_active: userData.is_active,
          user_types: userData.user_types || [],
          permissionKeys: userData.individualPermissions || [],
        };
        if (user?.company_id) {
          updateData.company_id = user.company_id;
        }
        await usersApi.updateUser(userData.id, updateData);
      } else {
        const profiles = userData.user_types || [];
        if (profiles.length === 0) {
          notify.error('Please assign at least one permission profile so the user can sign in.');
          return;
        }
        const createData: any = {
          email: userData.email,
          first_name: userData.first_name,
          last_name: userData.last_name,
          user_types: profiles,
          permissionKeys: userData.individualPermissions || [],
        };
        if (user?.company_id) {
          createData.company_id = user.company_id;
        }
        await usersApi.createUser(createData);
      }
      notify.success('User saved successfully!');
      loadUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      notify.error('Failed to save user');
    }
  };

  const handleDeactivateUser = (user: UserWithRoles) => {
    openModal({
      component: ConfirmModal,
      props: {
        title: 'Deactivate User',
        message: `Are you sure you want to deactivate ${user.first_name} ${user.last_name}? The user will lose access but all data will be preserved.`,
        confirmText: 'Deactivate User',
        cancelText: 'Cancel',
        type: 'warning',
        onConfirm: async () => {
          try {
            await usersApi.deactivateUser(user.id, 'Administrative deactivation');
            notify.success('User deactivated successfully!');
            loadUsers();
          } catch (error) {
            console.error('Error deactivating user:', error);
            notify.error('Failed to deactivate user');
          }
        },
        onCancel: () => {},
      },
    });
  };

  const handleReactivateUser = (user: UserWithRoles) => {
    openModal({
      component: ConfirmModal,
      props: {
        title: 'Reactivate User',
        message: `Are you sure you want to reactivate ${user.first_name} ${user.last_name}? The user will regain access.`,
        confirmText: 'Reactivate User',
        cancelText: 'Cancel',
        type: 'success',
        onConfirm: async () => {
          try {
            await usersApi.reactivateUser(user.id);
            notify.success('User reactivated successfully!');
            loadUsers();
          } catch (error) {
            console.error('Error reactivating user:', error);
            notify.error('Failed to reactivate user');
          }
        },
        onCancel: () => {},
      },
    });
  };

  const handleSuspendUser = (user: UserWithRoles) => {
    openModal({
      component: ConfirmModal,
      props: {
        title: 'Suspend User',
        message: `Are you sure you want to suspend ${user.first_name} ${user.last_name}? The user will be temporarily blocked.`,
        confirmText: 'Suspend User',
        cancelText: 'Cancel',
        type: 'warning',
        onConfirm: async () => {
          try {
            await usersApi.suspendUser(user.id, 'Administrative suspension');
            notify.success('User suspended successfully!');
            loadUsers();
          } catch (error) {
            console.error('Error suspending user:', error);
            notify.error('Failed to suspend user');
          }
        },
        onCancel: () => {},
      },
    });
  };

  const handleResetCredentials = (user: UserWithRoles) => {
    openModal({
      component: ConfirmModal,
      props: {
        title: 'Reset User Credentials',
        message: `Are you sure you want to reset ${user.first_name} ${user.last_name}'s password? They will need to set a new password on next login.`,
        confirmText: 'Reset Credentials',
        cancelText: 'Cancel',
        type: 'warning',
        onConfirm: async () => {
          try {
            await usersApi.resetUserCredentials(user.id);
            notify.success('User credentials reset successfully!');
            loadUsers();
          } catch (error) {
            console.error('Error resetting credentials:', error);
            notify.error('Failed to reset credentials');
          }
        },
        onCancel: () => {},
      },
    });
  };

  const handleImpersonateUser = async (user: UserWithRoles) => {
    if (!hasPermission('users.impersonate')) {
      notify.error('You do not have permission to impersonate users');
      return;
    }

    try {
      const response = await impersonationApi.startImpersonation({
        targetUserId: user.id,
      });

      if (response.success) {
        const impersonationUrl = `${window.location.origin}?impersonation_token=${response.data.token}`;
        window.open(impersonationUrl, '_blank');
        notify.success(`Impersonation session started for ${user.first_name} ${user.last_name}`);
      }
    } catch (error) {
      console.error('Error starting impersonation:', error);
      notify.error('Failed to start impersonation session');
    }
  };

  const handleViewSessions = async (user: UserWithRoles) => {
    try {
      const response = await usersApi.getUserSessions(user.id);
      if (response.success) {
        openModal({
          component: ({ sessions, userName }: { sessions: any[]; userName: string }) => (
            <div className="p-6 max-w-2xl">
              <h2 className="text-lg font-bold text-slate-900 mb-4">Active Sessions - {userName}</h2>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {sessions.length === 0 ? (
                  <div className="text-center text-slate-500 py-8">
                    <p className="text-sm">No active sessions found</p>
                  </div>
                ) : (
                  sessions.map((session: any, index: number) => {
                    const isCurrentSession = index === 0;
                    const deviceName = session.user_agent?.includes('Mobile')
                      ? 'Mobile Device'
                      : session.user_agent?.includes('iOS')
                        ? 'iOS Device'
                        : session.user_agent?.includes('Android')
                          ? 'Android Device'
                          : 'Desktop Device';

                    return (
                      <div
                        key={session.id}
                        className={`p-4 border border-slate-200 rounded-lg ${isCurrentSession ? 'bg-emerald-50/20' : 'bg-white'}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div
                              className={`h-2 w-2 rounded-full ${isCurrentSession ? 'bg-emerald-500 animate-pulse' : 'bg-slate-300'}`}
                            ></div>
                            <div>
                              <p
                                className={`text-sm font-bold ${isCurrentSession ? 'text-slate-900' : 'text-slate-700'}`}
                              >
                                {deviceName} {isCurrentSession ? '(Current)' : ''}
                              </p>
                              <p className="text-xs font-mono text-slate-400">
                                IP: {session.ip_address || 'Unknown'} -{' '}
                                {new Date(session.created_at).toLocaleString()}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span
                              className={`text-xs font-bold ${isCurrentSession ? 'text-slate-400' : 'text-slate-500'}`}
                            >
                              {session.is_active ? 'ACTIVE' : 'INACTIVE'}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          ),
          props: {
            sessions: response.data,
            userName: `${user.first_name} ${user.last_name}`,
          },
          options: { size: 'large' },
        });
      }
    } catch (error) {
      console.error('Error loading user sessions:', error);
      notify.error('Failed to load user sessions');
    }
  };

  const getUserStatus = (user: UserWithRoles): { status: string; duration: string } => {
    if (!user.is_active) {
      return { status: 'Inactive', duration: 'Account disabled' };
    }

    if (user.is_online) {
      const sessionCount = user.active_session_count || 0;
      return {
        status: 'Online',
        duration: `${sessionCount} active session${sessionCount !== 1 ? 's' : ''}`,
      };
    }

    if (user.last_login_at) {
      const lastLogin = new Date(user.last_login_at);
      const now = new Date();
      const minutesSinceLogin = Math.floor((now.getTime() - lastLogin.getTime()) / (1000 * 60));

      if (minutesSinceLogin <= 5) {
        return { status: 'Online', duration: 'Active now' };
      } else if (minutesSinceLogin < 60) {
        return { status: 'Away', duration: `Active ${minutesSinceLogin} min ago` };
      } else if (minutesSinceLogin < 1440) {
        const hours = Math.floor(minutesSinceLogin / 60);
        return { status: 'Away', duration: `Last seen ${hours} hour${hours !== 1 ? 's' : ''} ago` };
      } else if (minutesSinceLogin < 10080) {
        const days = Math.floor(minutesSinceLogin / (60 * 24));
        return { status: 'Offline', duration: `Last seen ${days} day${days !== 1 ? 's' : ''} ago` };
      }
    }

    if (user.expires_at) {
      const expiryDate = new Date(user.expires_at);
      const now = new Date();
      const daysUntilExpiry = Math.floor(
        (expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
      );

      if (expiryDate < now) {
        return { status: 'Offline', duration: `Expired ${Math.abs(daysUntilExpiry)}d ago` };
      } else if (daysUntilExpiry <= 7) {
        return { status: 'Away', duration: `Expires in ${daysUntilExpiry}d` };
      }
    }

    return { status: 'Offline', duration: 'No recent activity' };
  };

  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive' | 'online'>('all');

  const filteredUsers = useMemo(() => {
    let list = users;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      list = list.filter(
        (u) =>
          `${u.first_name} ${u.last_name}`.toLowerCase().includes(q) ||
          u.email.toLowerCase().includes(q),
      );
    }
    if (statusFilter === 'active') list = list.filter((u) => u.is_active);
    if (statusFilter === 'inactive') list = list.filter((u) => !u.is_active);
    if (statusFilter === 'online') list = list.filter((u) => u.is_online);
    return list;
  }, [users, searchTerm, statusFilter]);

  const canModifyUser = (targetUser: UserWithRoles): boolean => {
    if (targetUser.id === user?.id) return false;
    return isCompanyAdmin;
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="text-blue-600 animate-spin" size={40} />
      </div>
    );
  }

  if (!hasPermission('users.read')) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-3xl border border-slate-100 overflow-hidden">
          <div className="bg-red-600 px-8 py-6">
            <div className="flex items-center gap-4">
              <AlertTriangle className="text-white" size={28} />
              <div>
                <h1 className="text-xl font-bold text-white">Access Denied</h1>
                <p className="text-red-100 mt-1 text-sm">Restricted Area</p>
              </div>
            </div>
          </div>
          <div className="p-8">
            <p className="text-slate-600 text-sm">
              You do not have permission to access user management.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="text-blue-600 animate-spin" size={40} />
      </div>
    );
  }

  const onlineCount = users.filter((u) => u.is_online).length;
  const activeCount = users.filter((u) => u.is_active).length;
  const inactiveCount = users.filter((u) => !u.is_active).length;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900">Company Users</h1><PageInfoButton /></div>
              <span className="px-2.5 py-0.5 bg-slate-100 text-slate-600 text-xs font-semibold rounded-full">
                {pagination.total} members
              </span>
              {onlineCount > 0 && (
                <span className="flex items-center gap-1.5 px-2.5 py-0.5 bg-emerald-50 text-emerald-700 text-xs font-semibold rounded-full border border-emerald-200">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse inline-block" />
                  {onlineCount} online
                </span>
              )}
            </div>
            <p className="text-sm text-slate-500">
              Manage team members, permissions, and access control
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={loadUsers}
              className="bg-white border border-slate-200 text-slate-700 px-4 py-2.5 rounded-lg font-semibold text-sm hover:bg-slate-50 transition-colors flex items-center gap-2"
            >
              <RefreshCw size={16} />
              Refresh
            </button>
            {hasPermission('users.create') && (
              <button
                onClick={() => {
                  openModal({
                    component: AdminUserModal,
                    props: {
                      onSubmit: handleSaveUser,
                      onClose: () => {},
                      selectedUser: null,
                      availablePermissions,
                      availableCompanies: user?.company ? [user.company] : [],
                      hideCompanySelection: true,
                    },
                    options: { size: 'large' },
                  });
                }}
                className="bg-blue-600 text-white px-4 py-2.5 rounded-lg font-semibold text-sm hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <UserPlus size={16} />
                Add User
              </button>
            )}
          </div>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            {
              label: 'Total Members',
              value: pagination.total,
              icon: <Users size={18} className="text-blue-600" />,
              sub: `${activeCount} active · ${inactiveCount} inactive`,
            },
            {
              label: 'Active Users',
              value: activeCount,
              icon: <CheckCircle size={18} className="text-emerald-600" />,
              sub:
                pagination.total > 0
                  ? `${Math.round((activeCount / pagination.total) * 100)}% of team`
                  : '--',
            },
            {
              label: 'Online Now',
              value: onlineCount,
              icon: <Wifi size={18} className="text-emerald-600" />,
              sub: onlineCount > 0 ? 'Currently active' : 'No active sessions',
            },
            {
              label: 'Inactive',
              value: inactiveCount,
              icon: <WifiOff size={18} className="text-amber-600" />,
              sub: inactiveCount > 0 ? 'Access suspended' : 'All accounts active',
            },
          ].map((card) => (
            <div key={card.label} className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 bg-slate-50 rounded-lg flex items-center justify-center">
                  {card.icon}
                </div>
                <div>
                  <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
                    {card.label}
                  </p>
                  <p className="text-3xl font-bold text-slate-900 leading-none mt-0.5">{card.value}</p>
                </div>
              </div>
              <p className="text-xs text-slate-400">{card.sub}</p>
            </div>
          ))}
        </div>

        {/* Search & Filter */}
        <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden mb-4">
          <div className="px-5 py-3 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="relative flex-1">
                <Search
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  size={16}
                />
                <input
                  type="text"
                  placeholder="Search by name or email address..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm bg-slate-50 placeholder:text-slate-400"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs font-semibold bg-slate-100 hover:bg-slate-200 px-2 py-0.5 rounded transition-colors"
                  >
                    Clear
                  </button>
                )}
              </div>
              <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5 flex-shrink-0">
                {(
                  [
                    { key: 'all', label: 'All' },
                    { key: 'active', label: 'Active' },
                    { key: 'online', label: 'Online' },
                    { key: 'inactive', label: 'Inactive' },
                  ] as { key: typeof statusFilter; label: string }[]
                ).map((f) => (
                  <button
                    key={f.key}
                    onClick={() => setStatusFilter(f.key)}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                      statusFilter === f.key
                        ? 'bg-blue-600 text-white'
                        : 'text-slate-500 hover:text-slate-800 hover:bg-white'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
            <h3 className="text-sm font-bold text-slate-900">Team Members</h3>
            <span className="text-xs text-slate-400">
              {filteredUsers.length} result{filteredUsers.length !== 1 ? 's' : ''}
            </span>
          </div>

          <table className="w-full text-left border-collapse">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                <th className="pl-5 py-3 w-12">
                  <input type="checkbox" className="rounded border-slate-300" />
                </th>
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Permission Profile</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Last Login</th>
                <th className="px-4 py-3">IP Address</th>
                <th className="px-4 py-3">Member Since</th>
                <th className="px-4 py-3 text-right pr-5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-16 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center">
                        <Users size={28} className="text-slate-300" />
                      </div>
                      <p className="text-slate-600 font-semibold text-sm">No users found</p>
                      <p className="text-xs text-slate-400">
                        {searchTerm || statusFilter !== 'all'
                          ? 'Try adjusting your search or filter'
                          : 'No users in this company yet'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredUsers.map((userItem) => {
                  const { status, duration } = getUserStatus(userItem);
                  const isOnline = status === 'Online';
                  const isAway = status === 'Away';
                  const initials =
                    `${userItem.first_name?.[0] ?? ''}${userItem.last_name?.[0] ?? ''}`.toUpperCase();
                  const hue =
                    ((userItem.first_name?.charCodeAt(0) ?? 0) * 37 +
                      (userItem.last_name?.charCodeAt(0) ?? 0) * 13) %
                    360;

                  return (
                    <tr
                      key={userItem.id}
                      className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                    >
                      <td className="pl-5 py-4 text-left">
                        <input type="checkbox" className="rounded border-slate-300" />
                      </td>

                      {/* User */}
                      <td className="px-4 py-4 text-left">
                        <div className="flex items-center gap-3">
                          <div className="relative flex-shrink-0">
                            <div
                              className="w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm"
                              style={{
                                background: `hsl(${hue},65%,88%)`,
                                color: `hsl(${hue},55%,35%)`,
                              }}
                            >
                              {initials}
                            </div>
                            <div
                              className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${
                                isOnline
                                  ? 'bg-emerald-500'
                                  : isAway
                                    ? 'bg-amber-400'
                                    : 'bg-slate-300'
                              }`}
                            />
                          </div>
                          <div>
                            <div className="text-sm font-semibold text-slate-900">
                              {userItem.first_name} {userItem.last_name}
                            </div>
                            <div className="text-xs text-slate-500 font-mono">{userItem.email}</div>
                          </div>
                        </div>
                      </td>

                      {/* Permission profile */}
                      <td className="px-4 py-4 text-left">
                        <div className="flex flex-wrap gap-1">
                          {userItem.userTypes && userItem.userTypes.length > 0 ? (
                            userItem.userTypes.map((profileId) => (
                              <span
                                key={profileId}
                                className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200"
                              >
                                <Shield size={10} />
                                {permissionProfiles[profileId] || `Profile ${profileId}`}
                              </span>
                            ))
                          ) : (
                            <span className="text-xs text-slate-400 italic">No profile</span>
                          )}
                        </div>
                      </td>

                      {/* Status */}
                      <td className="px-4 py-4 text-left">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold border ${
                            isOnline
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                              : isAway
                                ? 'bg-amber-50 text-amber-700 border-amber-200'
                                : !userItem.is_active
                                  ? 'bg-red-50 text-red-600 border-red-200'
                                  : 'bg-slate-100 text-slate-500 border-slate-200'
                          }`}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${
                              isOnline
                                ? 'bg-emerald-500 animate-pulse'
                                : isAway
                                  ? 'bg-amber-400'
                                  : 'bg-slate-300'
                            }`}
                          />
                          <span>{status}</span>
                        </span>
                        <p className="text-[10px] text-slate-400 mt-1 pl-0.5">{duration}</p>
                      </td>

                      {/* Last login */}
                      <td className="px-4 py-4 text-left">
                        {userItem.last_login_at ? (
                          <div>
                            <div className="text-sm text-slate-700">
                              {new Date(userItem.last_login_at).toLocaleDateString('en-GB', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric',
                              })}
                            </div>
                            <div className="text-xs text-slate-400">
                              {new Date(userItem.last_login_at).toLocaleTimeString('en-GB', {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400 italic">Never logged in</span>
                        )}
                      </td>

                      {/* IP */}
                      <td className="px-4 py-4 text-left">
                        <span className="font-mono text-xs text-slate-500">
                          {userItem.last_login_ip || '--'}
                        </span>
                      </td>

                      {/* Created */}
                      <td className="px-4 py-4 text-left text-sm text-slate-500">
                        {new Date(userItem.created_at).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3 text-left pr-5">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => handleViewSessions(userItem)}
                            className="px-2 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded text-[9px] font-bold hover:bg-blue-100 transition-colors flex items-center gap-1"
                            title="View Sessions"
                          >
                            <Shield size={11} /> SESSIONS
                          </button>
                          {hasPermission('users.impersonate') && canModifyUser(userItem) && (
                            <button
                              onClick={() => handleImpersonateUser(userItem)}
                              className="px-2 py-1 bg-amber-50 text-amber-700 border border-amber-200 rounded text-[9px] font-bold hover:bg-amber-100 transition-colors flex items-center gap-1"
                              title="Impersonate"
                            >
                              <Eye size={11} /> VIEW AS
                            </button>
                          )}
                          {canModifyUser(userItem) && (
                            <button
                              onClick={() => {
                                const modalId = openModal({
                                  component: AdminUserModal,
                                  props: {
                                    onSubmit: handleSaveUser,
                                    onClose: () => closeModal(modalId),
                                    selectedUser: userItem,
                                    availablePermissions,
                                    availableCompanies: user?.company ? [user.company] : [],
                                    hideCompanySelection: true,
                                  },
                                  options: { size: 'large' },
                                });
                              }}
                              className="p-1.5 bg-white border border-slate-200 text-slate-500 rounded hover:border-blue-400 hover:text-blue-600 transition-colors"
                              title="Edit"
                            >
                              <Edit size={14} />
                            </button>
                          )}
                          {hasPermission('users.reset_credentials') && canModifyUser(userItem) && (
                            <button
                              onClick={() => handleResetCredentials(userItem)}
                              className="p-1.5 bg-white border border-slate-200 text-slate-500 rounded hover:border-orange-400 hover:text-orange-600 transition-colors"
                              title="Reset Password"
                            >
                              <RefreshCw size={14} />
                            </button>
                          )}
                          {userItem.is_active &&
                            (hasPermission('users.deactivate') ||
                              (user?.company_id && userItem.company_id === user.company_id)) &&
                            canModifyUser(userItem) && (
                              <button
                                onClick={() => handleDeactivateUser(userItem)}
                                className="p-1.5 bg-white border border-slate-200 text-slate-500 rounded hover:border-yellow-400 hover:text-yellow-600 transition-colors"
                                title="Deactivate"
                              >
                                <XCircle size={14} />
                              </button>
                            )}
                          {!userItem.is_active &&
                            (hasPermission('users.reactivate') ||
                              (user?.company_id && userItem.company_id === user.company_id)) &&
                            canModifyUser(userItem) && (
                              <button
                                onClick={() => handleReactivateUser(userItem)}
                                className="p-1.5 bg-white border border-slate-200 text-slate-500 rounded hover:border-emerald-400 hover:text-emerald-600 transition-colors"
                                title="Reactivate"
                              >
                                <CheckCircle size={14} />
                              </button>
                            )}
                          {hasPermission('users.suspend') && canModifyUser(userItem) && (
                            <button
                              onClick={() => handleSuspendUser(userItem)}
                              className="p-1.5 bg-white border border-slate-200 text-slate-500 rounded hover:border-red-400 hover:text-red-600 transition-colors"
                              title="Suspend"
                            >
                              <AlertTriangle size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>

          {/* Footer */}
          <div className="bg-slate-50 border-t border-slate-200 px-5 py-3 flex items-center justify-between">
            <div className="text-xs text-slate-400 flex items-center gap-4">
              <span>
                Showing{' '}
                <span className="font-semibold text-slate-600">{filteredUsers.length}</span> of{' '}
                <span className="font-semibold text-slate-600">{pagination.total}</span> users
              </span>
              {onlineCount > 0 && (
                <span className="flex items-center gap-1 text-emerald-600">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse inline-block" />
                  {onlineCount} online now
                </span>
              )}
            </div>
            <button
              onClick={loadUsers}
              className="text-xs font-semibold text-blue-600 hover:text-blue-800 flex items-center gap-1.5 transition-colors"
            >
              <RefreshCw size={12} /> Reload
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyUsers;
